import * as CryptoJS from 'crypto-js';
import { scrypt } from 'scrypt-js';
import { environment } from '../../environments/environment'; 
import { Injectable } from '@angular/core';

const salt = new TextEncoder().encode('salt'); // must match backend
const ivLength = 16;

// derive 32-byte key using scrypt (same as Node.js scryptSync)
async function deriveKey(password: string): Promise<CryptoJS.lib.WordArray> {
  const passwordBytes = new TextEncoder().encode(password);

  // Node default params: N=16384, r=8, p=1, dkLen=32
  const N = 16384, r = 8, p = 1, dkLen = 32;

  const derivedKey = await scrypt(passwordBytes, salt, N, r, p, dkLen);
  return CryptoJS.lib.WordArray.create(derivedKey as any);
}

@Injectable({ providedIn: 'root' })
export class EncryptionService {
  async encryptData(data: any): Promise<string> {
    try {
      const key = await deriveKey(environment.ENCRYPTION_KEY);
      // console.log("Derived Key:", key.toString());

      // generate random IV (16 bytes)
      const iv = CryptoJS.lib.WordArray.random(ivLength);
      // console.log("Generated IV:", iv.toString());

      // convert object to string
      const jsonData = typeof data === 'string' ? data : JSON.stringify(data);
      console.log("JSON Data to Encrypt:", jsonData);

      // AES-256-CBC encryption
      const encrypted = CryptoJS.AES.encrypt(jsonData, key, {
        iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });
      // console.log("Encrypted Data:", encrypted.toString());

      const encrypt = iv.toString(CryptoJS.enc.Hex) + ':' + encrypted.ciphertext.toString(CryptoJS.enc.Hex);
      // console.log("Final Encrypted Payload (IV:Ciphertext):", encrypt);

      return encrypt;
    } catch (error) {
      console.error('Encryption failed:', error);
      throw new Error('Failed to encrypt data');
    }
  }

  async decryptData(data: string): Promise<any> {
    try {
      if (!data || typeof data !== 'string') {
        throw new Error('Invalid encrypted data: data is null, undefined, or not a string');
      }

      const key = await deriveKey(environment.ENCRYPTION_KEY);

      const parts = data.split(':');
      if (parts.length !== 2) {
        throw new Error(`Invalid encrypted data format: expected 2 parts, got ${parts.length}`);
      }

      const iv = CryptoJS.enc.Hex.parse(parts[0]);
      const encryptedHex = CryptoJS.enc.Hex.parse(parts[1]);
      const ciphertext = CryptoJS.lib.CipherParams.create({ ciphertext: encryptedHex });

      const decrypted = CryptoJS.AES.decrypt(ciphertext, key, {
        iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });

      const result = decrypted.toString(CryptoJS.enc.Utf8);
      
      if (!result) {
        throw new Error('Decryption failed: empty result');
      }

      // console.log("Decrypted Result:", result);

      try {
        const parsed = JSON.parse(result);
        console.log("Parsed JSON Result:", parsed);
        return parsed;
      } catch (parseError) {
        console.log("Not JSON, returning as string:", result);
        return result;
      }
    } catch (error) {
      console.error('Decryption failed:', error);
      throw new Error(`Failed to decrypt data`);
    }
  }
}